import { useNavigate } from "react-router";

import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { buildUrl } from "@/components/helpers/url-utils";
import { isProgramLead } from "@/components/helpers/workspace/permissions";
import { OneOneMeetingSectionItem } from "@/components/pages_logged_in/program/components/program-component/panels/OneOneMeetingSectionItem";
import { CoachSelectionAfterIntro } from "@/components/pages_logged_in/program/components/program-component/panels/one-one-coaching/CoachSelectionAfterIntro";
import { OneOneSessionStats } from "@/components/pages_logged_in/program/components/set-components/OneOneSessionStats";
import { sortComponentsByDate } from "@/components/pages_logged_in/program/components/set-components/sort-components-by-date";
import { ViewComponentButton } from "@/components/pages_logged_in/program/components/ViewComponentButton";
import { PrimaryButton } from "@/components/pieces/button/PrimaryButton";
import { Avatar } from "@/components/pieces/users/Avatar";
import { Callout } from "@/components/ui/callout";
import { INNER_BORDER_COLOR } from "@/css/constants";
import { ComponentStatus, ComponentTypeEnum, MeetingStatus } from "@/graphql";
import {
  isParticipantComponent,
  LeadComponent,
  MainComponent,
  PanelProps,
  ParticipantComponent,
} from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

import { OneOneSession } from "./one-one-coaching/OneOneSession";

type ChildComponent = NonNullableArrayItem<
  ParticipantComponent["childComponents"]
>;

type Component = (ParticipantComponent | LeadComponent | ChildComponent) & {
  isIntroComponentCompleted?: boolean;
  selectedCoach?: ParticipantComponent["selectedCoach"];
  product?: ChildComponent["product"];
};

interface OneOneSessionItemProps {
  components: Component[];
  selectedCoach: ParticipantComponent["selectedCoach"];
}

function OneOneSessionItem({
  components,
  selectedCoach,
}: OneOneSessionItemProps) {
  const navigate = useNavigate();

  const ongoingComponents = components.filter(
    (item) => item.status === ComponentStatus.Ongoing,
  );

  const firstUpcomingComponents = sortComponentsByDate(components)
    .filter((item) => item.status === ComponentStatus.Upcoming)
    .slice(0, 1);

  const displayComponents = sortComponentsByDate(
    ongoingComponents.length > 0 ? ongoingComponents : firstUpcomingComponents,
  );

  return displayComponents.map((item) => {
    const { product } = item;
    return (
      <div
        className="round-corners xs:gap-4 xs:p-4 flex flex-col bg-gradient-to-br from-[#F2FAFF] to-[#D8ECF8] md:gap-6 md:p-6"
        key={item.id}
      >
        <div className="text-lg font-semibold text-blue-800">
          It's time to schedule {item?.name}
        </div>
        <div
          className="xs:p-4 xs:gap-3.5 round-corners flex bg-white md:gap-4 md:p-6"
          style={{
            border: `1px solid ${INNER_BORDER_COLOR}`,
          }}
        >
          <div className="xs:hidden md:block">
            <Avatar user={selectedCoach} size={50} />
          </div>

          <div className="xs:block md:hidden">
            <Avatar user={selectedCoach} size={40} />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <div className="text-base font-semibold">{selectedCoach?.name}</div>
            <div className="text-base font-normal text-gray-500">
              {product?.name || "Last session: None"}
            </div>
          </div>
        </div>
        <div>
          <PrimaryButton
            onClick={() => {
              const createOrderUrl = buildUrl(
                `/schedule/${selectedCoach?.id}`,
                {
                  data: {
                    programComponentId: item.id,
                  },
                },
              );
              navigate(createOrderUrl);
            }}
            style={{ minWidth: 160 }}
          >
            Schedule
          </PrimaryButton>
        </div>
      </div>
    );
  });
}

type ProgramComponentType = (
  | LeadComponent
  | ParticipantComponent
  | MainComponent
) & {
  isIntroComponentCompleted?: boolean;
  selectedCoach?: ParticipantComponent["selectedCoach"];
  childComponents?: ParticipantComponent["childComponents"];
  coachingStatistic?: LeadComponent["coachingStatistic"];
  program?: LeadComponent["program"];
  visibleUpcomingQuestions?: boolean;
};

export function OneOneMeetingItemPanel({
  component,
  view = "list",
}: PanelProps) {
  const {
    participantGuidance,
    childComponents,
    status,
    selectedCoach,
    program,
    coachingStatistic,
  } = component as ProgramComponentType;
  // This component supports only OneOneCoachingMaster component
  // Leads will show all child components
  // Participants will show only the session components, not show inro component
  const isParticipantView = isParticipantComponent(component);
  const isOngoing = status === ComponentStatus.Ongoing;

  const isMasterComponent =
    component?.type === ComponentTypeEnum.OneOneCoachingMaster;

  const canViewStatistics =
    !isParticipantView &&
    isMasterComponent &&
    [ComponentStatus.Ongoing, ComponentStatus.Completed].includes(status);

  const introProgramComponent = childComponents?.find(
    (childComponent) =>
      childComponent.type === ComponentTypeEnum.OneOneCoachingIntro,
  );

  const sessionComponents = ensureNonNullArray(
    childComponents?.filter(
      (childComponent) =>
        childComponent.type === ComponentTypeEnum.OneOneCoachingSession,
    ),
  );

  const canBookMeetingComponents = isParticipantView
    ? sessionComponents?.filter(
        (childComponent) =>
          [ComponentStatus.Ongoing, ComponentStatus.Upcoming].includes(
            childComponent.status,
          ) &&
          childComponent?.meetings?.filter(
            (meeting) =>
              meeting.status &&
              ![MeetingStatus.Canceled].includes(meeting.status),
          ).length === 0,
      )
    : sessionComponents;

  const canScheduleSession =
    canBookMeetingComponents?.length > 0 && selectedCoach;

  const completedSessionComponents = isParticipantView
    ? sessionComponents?.filter(
        (childComponent) => childComponent.status === ComponentStatus.Completed,
      )
    : [];
  const completedAll =
    completedSessionComponents.length > 0 &&
    completedSessionComponents.length === sessionComponents.length;

  const sortedSessions = sortComponentsByDate(sessionComponents);

  const upcomingSessionComponents = sessionComponents?.filter(
    (childComponent) =>
      childComponent.status === ComponentStatus.Upcoming &&
      !!childComponent?.meetings?.find(
        (meeting) => meeting.status === MeetingStatus.Upcoming,
      ),
  );

  const {
    totalParticipants,
    onTrackPercentage,
    averageRating,
    completedSessionsCount,
    totalCreditsSpent,
    activeCoachesCount,
    ratingCount,
  } = coachingStatistic || {};

  const programId = program?.id;

  if (!isMasterComponent) {
    return null;
  }

  if (view === "feed") {
    return (
      <>
        {canScheduleSession && isParticipantView && (
          <OneOneSessionItem
            components={canBookMeetingComponents}
            selectedCoach={component?.selectedCoach}
          />
        )}
        {upcomingSessionComponents?.map((component) => (
          <OneOneSession
            key={component.id}
            meeting={
              component.meetings?.find(
                (meeting) => meeting.status === MeetingStatus.Upcoming,
              )!
            }
          />
        ))}
        {introProgramComponent && isOngoing && isParticipantView && (
          <CoachSelectionAfterIntro
            component={introProgramComponent}
            selectedCoach={component?.selectedCoach}
          />
        )}
      </>
    );
  }
  return (
    <div className="xs:gap-3.5 flex flex-col md:gap-4 empty:hidden">
      {isParticipantView ? (
        <>
          {completedAll && (
            <Callout variant="success" weight="bold" size="lg">
              All sessions completed
            </Callout>
          )}
          {status === ComponentStatus.Ongoing && !selectedCoach && (
            <Callout variant="info">
              You will be able to schedule regular sessions after completing at
              least one intro session and selecting a coach.
            </Callout>
          )}
          {introProgramComponent && isOngoing && (
            <CoachSelectionAfterIntro
              component={introProgramComponent}
              selectedCoach={component?.selectedCoach}
            />
          )}
        </>
      ) : null}
      {canViewStatistics && (
        <>
          <OneOneSessionStats
            totalParticipants={totalParticipants ?? 0}
            onTrackPercentage={onTrackPercentage ?? 0}
            averageRating={averageRating ?? 0}
            completedSessionsCount={completedSessionsCount ?? 0}
            totalCreditsSpent={totalCreditsSpent ?? 0}
            activeCoachesCount={activeCoachesCount ?? 0}
            ratingCount={ratingCount ?? 0}
          />
          <ViewComponentButton
            programId={programId ?? ""}
            component={component}
            intent="primary"
            isProgramLead={isProgramLead(programId)}
          />
        </>
      )}

      {canScheduleSession && isParticipantView && (
        <OneOneSessionItem
          components={canBookMeetingComponents}
          selectedCoach={component?.selectedCoach}
        />
      )}

      {isParticipantView && (
        <>
          {<hr className="m-0 border-neutral-300 first:hidden" />}
          <div className="flex flex-col gap-2">
            <div className="text-lg font-semibold text-gray-800">
              Coaching Journey
            </div>
            <div className="text-sm font-normal text-gray-700">
              Sessions must be held within specific time windows, but you can
              book in advance
            </div>
          </div>
        </>
      )}

      {participantGuidance && (
        <RichContentRenderer content={participantGuidance} />
      )}

      {!canViewStatistics && (
        <div className="xs:gap-2.5 flex flex-col md:gap-3">
          {introProgramComponent && !isParticipantView && (
            <OneOneMeetingSectionItem
              component={introProgramComponent}
              isParticipantView={isParticipantView}
            />
          )}
          {sortedSessions.map((component) => (
            <OneOneMeetingSectionItem
              key={component.id}
              component={component}
              isParticipantView={isParticipantView}
            />
          ))}
        </div>
      )}
    </div>
  );
}
